// src/app/appPages.js
// App.js'ten ayrıştırılan sayfa render mantığı
// History API (pushState/popstate) olduğu gibi App.js'te kalır — bu dosya sadece JSX render'ı taşır

import { lazy } from "react";
import type { DocumentData } from "firebase/firestore";
import MapErrorBoundary from "../components/MapErrorBoundary";
import Feed from "../Feed";
// PERF: Explore artık LAZY → ana bundle'dan çıkar (PostDetailModal +
// ProfilePostViewerMobile + browser-image-compression zincirini de geciktirir).
// renderPageContent zaten App.tsx'teki sayfa <Suspense> içinde → fallback hazır.
// `: any` KALDIRILDI — `Explore.tsx` artık tipli ve kümede (0 hata).
const Explore = lazy(() => import("../Explore"));
// EMİR #76 — İlk Işıklar keşif galerisi (lazy; ana bundle'dan çıkar).
const DiscoveriesPage: any = lazy(() => import("../DiscoveriesPage"));

// P1-4 (PROFIL_DERIN_ANALIZ_V2 §4.1) — Profile route code-split.
// Profil ailesi (~1950 satır: Profile + ProfileMobile + ProfileDesktop)
// ana bundle'dan çıkarıldı; App.js'teki mevcut Suspense boundary
// fallback'i karşılar. Diğer lazy route'larla aynı desen.
const Profile           = lazy(() => import("../Profile"));
// `: any` KALDIRILDI (2026-08-07). O `any`, `<MyClips onNavChange={…} />`
// çağrısındaki İMZADA OLMAYAN prop'u sessizce yutuyordu (#763'te
// raporlanmıştı). Artık sözleşmeyi derleyici denetliyor.
const MyClips           = lazy(() => import("../MyClips"));
const MyClipsV3Gate: any = lazy(() => import("../myclipsv3/rollout/MyClipsV3Gate") as any);
const MapDesktop        = lazy(() => import("../MapDesktop"));
const MapMobile: any    = lazy(() => import("../MapMobile"));
const MyLiveApp         = lazy(() => import("../pages/MyLive/MyLiveApp"));
const AdminReports        = lazy(() => import("../pages/AdminReports"));
const AdminCostDashboard  = lazy(() => import("../pages/AdminCostDashboard"));
const CreatorDashboard  = lazy(() => import("../pages/CreatorDashboard"));
const BlockedUsers      = lazy(() => import("../pages/BlockedUsers"));
const GizliKelimeler    = lazy(() => import("../pages/GizliKelimeler"));
const OneriAyarlari     = lazy(() => import("../pages/OneriAyarlari"));

// ── TİPLER (import'lardan SONRA, ilk kullanımdan ÖNCE) ──────────────────
// 📏 ÇAĞRI YERİ SAYIMI: `renderPageContent` → 1 çağrı (App.tsx:818).
// İmzalar UYDURULMADI, App.tsx'teki ÜRETİCİ bildirimlerinden birebir alındı
// (`handleViewComments` App.tsx:666, `handleViewClip` :695, `pushStoryUrl` :439,
// `handleViewPlaceDetail` :732, `handleNavChange` :497). Gövdeden DAR yazılsaydı
// borç silinmez, App.tsx'e taşınırdı.

/** App.tsx'in `renderPage({ handlers: { … } })` nesnesiyle birebir aynı 8 alan. */
type SayfaHandlers = {
  handleViewProfile: (userId?: string) => void;
  handleViewComments: (post: { id?: string } | null | undefined) => void;
  handleViewClip: (clip: { id?: string } | null | undefined) => void;
  /** `uid?` UYDURMA DEĞİL, ölçüm: tek tüketici `MapDesktop.onViewStory`
   *  `HaritaArkadasi` (= `DocumentData & { uid: string }`) gönderir. App.tsx:439
   *  `pushStoryUrl` yalnız `kullaniciAdi/username/stories` okur — hepsi opsiyonel,
   *  yani ZAYIF TİP; `DocumentData`nın index imzası zayıf tipi karşılamadığı için
   *  TS "ortak alan yok" der. `uid?` gerçekte GÖNDERİLEN alanı yazar, tipleri
   *  bağlar ve App.tsx'e hata sızdırmaz (fazladan opsiyonel alan yutulur). */
  handleViewStory: (
    data:
      | {
          uid?: string;
          kullaniciAdi?: string;
          username?: string;
          stories?: Array<{ id?: string }>;
        }
      | null
      | undefined,
  ) => void;
  handleViewPlaceDetail: (
    placeData:
      | { placeId?: string; refId?: string; id?: string; name?: string | null }
      | null
      | undefined,
  ) => void;
  handleNavChange: (tab: string) => void;
  setActivePage: (page: string) => void;
  handleStartMessage: (userId?: string) => void;
};

type SayfaParams = {
  activePage: string;
  /** Firebase `User`; gövde yalnız `uid` okur ve `if (!user) return null` ile korur. */
  user: { uid: string } | null | undefined;
  /** Firestore profil belgesi. `DocumentData` = SDK'nın kendi belge sözleşmesi
   *  (MapDesktop.tsx:177 ile AYNI tip) — `any` elle yazılmadı, alt tüketici
   *  `MapDesktop` zaten bu tipi bekliyor. */
  currentUserProfile: DocumentData | null | undefined;
  profileRouteUsername: string | null | undefined;
  isMobile: boolean;
  handlers: SayfaHandlers;
};

/**
 * renderPageContent — aktif sayfaya göre doğru bileşeni döndürür.
 *
 * @param {object} params
 * @param {string}  params.activePage
 * @param {object}  params.user
 * @param {object}  params.currentUserProfile
 * @param {string|null} params.profileRouteUsername
 * @param {boolean} params.isMobile
 * @param {object}  params.handlers — handleViewProfile, handleViewComments, handleViewClip, handleViewStory, handleViewPlaceDetail, handleNavChange, setActivePage
 */
export function renderPageContent({
  activePage,
  user,
  currentUserProfile,
  profileRouteUsername,
  isMobile,
  handlers,
}: SayfaParams) {
  if (!user) return null;

  const {
    handleViewProfile,
    handleViewComments,
    handleViewClip,
    handleViewStory,
    handleViewPlaceDetail,
    handleNavChange,
    setActivePage,
    handleStartMessage,
  } = handlers;

  // ══════════════════════════════════════════════════════════════════════
  // KAPI BEYANI — her `case` ÜSTÜNDE, yazarın gördüğü yerde.
  //
  // Profil dokümanı okunamayınca kapı (App.tsx) tam-ekran devralır. Kapı artık
  // GLOBAL değil: `src/app/profilKapisi.ts` hangi sayfanın devralınacağını
  // söyler. VARSAYILAN KAPALI — beyan edilmemiş her sayfa kapılıdır.
  //
  // Aşağıdaki her `case`in hemen üstünde tek satırlık bir beyan var:
  //     // KAPI: profilsiz-çalışır  → profilKapisi.PROFILSIZ_CALISIR
  //     // KAPI: profil-zorunlu     → profilKapisi.PROFIL_ZORUNLU
  // `profilKapisi.test.ts` bu dosyayı OKUR: beyansız `case` ya da tabloyla
  // çelişen beyan testi DÜŞÜRÜR. Yani yeni sayfa ekleyen kişi "görür" değil,
  // KARAR VERMEK ZORUNDA KALIR. Gerekçe (ölçüm sayıları) tabloda.
  // ══════════════════════════════════════════════════════════════════════
  switch (activePage) {
    // KAPI: profilsiz-çalışır
    case "home":
      return (
        <Feed
          onUserClick={handleViewProfile}
          onCommentClick={handleViewComments}
          onViewClip={handleViewClip}
          onOpenPlace={handleViewPlaceDetail}
          aktifKullaniciId={user.uid}
        />
      );

    // KAPI: profilsiz-çalışır
    case "explore":
      return (
        <Explore
          onUserClick={handleViewProfile}
          aktifKullaniciId={user.uid}
          // Masaüstünde gönderi/klip açılışı uygulamanın KENDİ modal hattından
          // gider → URL (`/p/{id}` · `/c/{id}`), geri tuşu, cold-load ve
          // görünür sarmalayıcı hepsi hazır gelir. Explore'un yerel modalı
          // bunların hiçbirini taşımıyordu (gözle ölçüldü, 2026-08-24).
          onOpenPost={handleViewComments}
          onOpenClip={handleViewClip}
        />
      );

    // KAPI: profilsiz-çalışır
    case "discoveries":
      return <DiscoveriesPage aktifKullaniciId={user.uid} onUserClick={handleViewProfile} />;

    // KAPI: profilsiz-çalışır
    case "map":
      return isMobile ? (
        <MapMobile
          currentUserProfile={currentUserProfile}
          onViewStory={handleViewStory}
          onUserClick={handleViewProfile}
          onOpenPlace={handleViewPlaceDetail}
        />
      ) : (
        /* DENETİM FIX — boundary mobilde vardı, desktop'ta yoktu (asimetri) */
        <MapErrorBoundary compact>
          <MapDesktop
            currentUserProfile={currentUserProfile}
            onViewStory={handleViewStory}
            onUserClick={handleViewProfile}
            onOpenPlace={handleViewPlaceDetail}
          />
        </MapErrorBoundary>
      );

    // KAPI: profilsiz-çalışır
    case "clips":
      // İki kanal router:
      //   V3 flag "on"/rollout → MyClipsV3 (prod'da varsayılan "on")
      //   aksi → legacy MyClips (rollback fallback)
      // ⚠️ `legacy={<MyClips onNavChange={handleNavChange} />}` idi;
      //    `onNavChange` KALDIRILDI (ölçüm): `MyClips.tsx` bu prop'u
      //    imzasında taşımıyor ve gövdesinde hiç okumuyor —
      //    `const MyClips: any` onu sessizce yutuyordu (#763'te
      //    raporlanmıştı, `any` kalkınca derleyici doğruladı).
      //    Prop ÖLÜ DEĞİL: hemen aşağıda gerçek tüketiciye
      //    (`MyClipsV3Gate` → `ClipNavBar`/`FeedContainerV3`) veriliyor.
      //    Ölü olan yalnız bu ikinci kopyaydı.
      return (
        <MyClipsV3Gate
          uid={user?.uid}
          onNavChange={handleNavChange}
          profilePic={currentUserProfile?.profilFoto}
          legacy={<MyClips />}
        />
      );

    // KAPI: profil-zorunlu
    case "profile": {
      const myUsername =
        currentUserProfile?.kullaniciAdi ||
        currentUserProfile?.username ||
        "";
      const isOwnProfileRoute =
        !profileRouteUsername || profileRouteUsername === myUsername;
      return (
        <Profile
          ownUid={isOwnProfileRoute ? user.uid : null}
          onUserClick={handleViewProfile}
          onPlaceClick={handleViewPlaceDetail}
          onSendMessage={handleStartMessage}
        />
      );
    }

    // KAPI: profil-zorunlu
    case "adminReports":
      return <AdminReports />;

    // KAPI: profil-zorunlu
    case "adminCostDashboard":
      return <AdminCostDashboard />;

    // KAPI: profilsiz-çalışır
    case "creatorDashboard":
      return <CreatorDashboard />;

    // KAPI: profilsiz-çalışır
    case "blockedUsers":
      return <BlockedUsers />;

    // /settings/hidden-words — gizli kelimeler
    // KAPI: profilsiz-çalışır
    case "gizliKelimeler":
      return <GizliKelimeler />;

    // /settings/suggestions — öneri sıfırlama
    // KAPI: profilsiz-çalışır
    case "oneriAyarlari":
      return <OneriAyarlari />;

    // KAPI: profil-zorunlu
    case "mylive":
      return <MyLiveApp onClose={() => setActivePage("home")} />;

    default:
      return (
        <Feed
          onUserClick={handleViewProfile}
          onCommentClick={handleViewComments}
          onViewClip={handleViewClip}
          onOpenPlace={handleViewPlaceDetail}
          aktifKullaniciId={user.uid}
        />
      );
  }
}
